/**
 * Persistent reader reactions, separate from the room's three-second floating reactions.
 *
 * Marks are applied as absolutely-positioned pseudo-elements on the canonical `[data-s]` /
 * `[data-b]` nodes. They therefore do not add characters to the multicolumn flow, change a page
 * measurement, install per-sentence event handlers, or compete with selection and long press. The
 * sentence menu is the only interactive surface and gives the same state an accessible text list.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from "react";

import { blockIdFrom } from "../../../shared/address.ts";
import { ApiError, api } from "../lib/api.ts";

export const REACTIONS = [
  { id: "heart", emoji: "❤️", label: "Love" },
  { id: "laugh", emoji: "😂", label: "Funny" },
  { id: "wow", emoji: "😮", label: "Wow" },
  { id: "sad", emoji: "😢", label: "Moving or sad" },
  { id: "clap", emoji: "👏", label: "Applause" },
  { id: "idea", emoji: "💡", label: "Interesting idea" },
] as const;

export type ReactionValue = (typeof REACTIONS)[number]["id"];
export type ReactionTargetKind = "sentence" | "block";

export interface ReaderReaction {
  targetKind: ReactionTargetKind;
  targetId: string;
  reaction: ReactionValue;
  actor: { name: string; kind: "user" | "guest" };
  mine: boolean;
  createdAt: number;
  updatedAt: number;
}

interface ReactionResponse {
  bookId: string;
  editionId: string;
  reactions: ReaderReaction[];
}

const CHANGE_EVENT = "ab:reader-reaction-change";
const POLL_MS = 10_000;
const STYLE_ID = "ab-persistent-reactions-css";

function query(args: {
  editionId: string;
  chapterOrd?: number;
  targetKind?: ReactionTargetKind;
  targetId?: string;
  roomCode?: string | null;
}): string {
  const p = new URLSearchParams({ editionId: args.editionId });
  if (args.chapterOrd !== undefined) p.set("chapterOrd", String(args.chapterOrd));
  if (args.targetKind !== undefined) p.set("targetKind", args.targetKind);
  if (args.targetId !== undefined) p.set("targetId", args.targetId);
  if (args.roomCode) p.set("roomCode", args.roomCode);
  return p.toString();
}

function endpoint(bookId: string, target?: { kind: ReactionTargetKind; id: string }): string {
  const base = `/books/${encodeURIComponent(bookId)}/reactions`;
  if (!target) return base;
  return `${base}/${encodeURIComponent(target.kind)}/${encodeURIComponent(target.id)}`;
}

function announceChange(bookId: string, editionId: string): void {
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { bookId, editionId } }));
}

function reactionMeta(value: ReactionValue) {
  return REACTIONS.find((r) => r.id === value) ?? REACTIONS[0];
}

export const reactionApi = {
  listChapter(bookId: string, editionId: string, chapterOrd: number, roomCode?: string | null): Promise<ReactionResponse> {
    return api.get(`${endpoint(bookId)}?${query({ editionId, chapterOrd, roomCode })}`);
  },
  listTarget(
    bookId: string,
    editionId: string,
    target: { kind: ReactionTargetKind; id: string },
    roomCode?: string | null,
  ): Promise<ReactionResponse> {
    return api.get(
      `${endpoint(bookId)}?${query({ editionId, targetKind: target.kind, targetId: target.id, roomCode })}`,
    );
  },
  put(
    bookId: string,
    editionId: string,
    target: { kind: ReactionTargetKind; id: string },
    reaction: ReactionValue,
    roomCode?: string | null,
  ): Promise<ReactionResponse> {
    return api.put(endpoint(bookId, target), { editionId, reaction, roomCode: roomCode ?? undefined });
  },
  remove(
    bookId: string,
    editionId: string,
    target: { kind: ReactionTargetKind; id: string },
    roomCode?: string | null,
  ): Promise<ReactionResponse> {
    return api.del(`${endpoint(bookId, target)}?${query({ editionId, roomCode })}`);
  },
};

function injectCss(): void {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = `
/* Absolute, so a reaction never changes multicolumn pagination or selection geometry. */
[data-ab-sentence-reactions], [data-ab-block-reactions] { position: relative; }
[data-ab-sentence-reactions]::after,
[data-ab-block-reactions]::before {
  position: absolute;
  z-index: 2;
  padding: 0.08em 0.36em;
  color: var(--read-ink);
  background: var(--read-bg);
  border: 1px solid var(--read-rule);
  border-radius: var(--radius-pill);
  box-shadow: var(--shadow-soft, none);
  font-family: var(--font-ui);
  font-size: 0.64em;
  line-height: 1.35;
  white-space: nowrap;
  pointer-events: none;
}
[data-ab-sentence-reactions]::after {
  content: attr(data-ab-sentence-reactions);
  inset-inline-end: 0;
  top: -1.1em;
}
[data-ab-block-reactions]::before {
  content: attr(data-ab-block-reactions);
  inset-inline-start: 0;
  top: -1.1em;
}
@media (prefers-reduced-motion: no-preference) {
  [data-ab-sentence-reactions]::after,
  [data-ab-block-reactions]::before { transition: opacity 120ms var(--ease-out, ease-out); }
}
`;
  document.head.appendChild(style);
}

function grouped(rows: readonly ReaderReaction[]): Map<string, { visible: string; description: string }> {
  const byTarget = new Map<string, ReaderReaction[]>();
  for (const row of rows) {
    const key = `${row.targetKind}:${row.targetId}`;
    const found = byTarget.get(key);
    if (found) found.push(row);
    else byTarget.set(key, [row]);
  }

  const out = new Map<string, { visible: string; description: string }>();
  for (const [key, targetRows] of byTarget) {
    const counts = new Map<ReactionValue, number>();
    for (const row of targetRows) counts.set(row.reaction, (counts.get(row.reaction) ?? 0) + 1);
    const tokens = REACTIONS.flatMap((meta) => {
      const count = counts.get(meta.id) ?? 0;
      return count === 0 ? [] : [`${meta.emoji}${count > 1 ? ` ${count}` : ""}`];
    });
    const names = targetRows.map((row) => `${row.actor.name}: ${reactionMeta(row.reaction).label}`);
    out.set(key, {
      visible: tokens.slice(0, 3).join(" ") + (tokens.length > 3 ? ` +${tokens.length - 3}` : ""),
      description: `${targetRows.length} reader reaction${targetRows.length === 1 ? "" : "s"}. ${names.join("; ")}.`,
    });
  }
  return out;
}

function applyMarks(track: HTMLElement, rows: readonly ReaderReaction[]): void {
  for (const el of track.querySelectorAll<HTMLElement>("[data-ab-sentence-reactions], [data-ab-block-reactions]")) {
    el.removeAttribute("data-ab-sentence-reactions");
    el.removeAttribute("data-ab-block-reactions");
    el.removeAttribute("aria-description");
  }

  const marks = grouped(rows);
  for (const el of track.querySelectorAll<HTMLElement>("[data-s], [data-b]")) {
    const sid = el.getAttribute("data-s");
    const blockId = el.getAttribute("data-b");
    const kind: ReactionTargetKind = sid ? "sentence" : "block";
    const id = sid ?? blockId;
    if (!id) continue;
    const mark = marks.get(`${kind}:${id}`);
    if (!mark) continue;
    el.setAttribute(kind === "sentence" ? "data-ab-sentence-reactions" : "data-ab-block-reactions", mark.visible);
    el.setAttribute("aria-description", mark.description);
  }
}

/**
 * Fetches inside its own component so a ten-second room refresh never re-renders Reader's thousands
 * of sentence spans. The only mutation is our namespaced data/ARIA attributes.
 */
export function PersistentReactionMarks(props: {
  track: HTMLElement | null;
  bookId: string;
  editionId: string | null;
  chapterOrd: number | null;
  roomCode?: string | null;
}): ReactElement | null {
  const { track, bookId, editionId, chapterOrd, roomCode = null } = props;
  const [rows, setRows] = useState<ReaderReaction[]>([]);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!editionId || chapterOrd === null) return;
    const seq = ++requestSeq.current;
    try {
      const next = await reactionApi.listChapter(bookId, editionId, chapterOrd, roomCode);
      if (seq === requestSeq.current) setRows(next.reactions);
    } catch {
      // Reactions are supplementary. A failed poll never turns a readable page into an error state.
    }
  }, [bookId, editionId, chapterOrd, roomCode]);

  useEffect(() => {
    setRows([]);
    void load();
    const id = window.setInterval(() => void load(), POLL_MS);
    const onFocus = () => void load();
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ bookId?: string; editionId?: string }>).detail;
      if (detail?.bookId === bookId && detail.editionId === editionId) void load();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      // An old chapter's response must not paint its ids onto the new chapter while navigation is in
      // flight. `api.get` accepts a signal, but the monotonically increasing guard also covers a
      // response that won the race before abort could be observed.
      requestSeq.current++;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener(CHANGE_EVENT, onChange);
    };
  }, [bookId, editionId, load]);

  useEffect(() => {
    if (!track) return;
    injectCss();
    const apply = () => applyMarks(track, rows);
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(track, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      for (const el of track.querySelectorAll<HTMLElement>("[data-ab-sentence-reactions], [data-ab-block-reactions]")) {
        el.removeAttribute("data-ab-sentence-reactions");
        el.removeAttribute("data-ab-block-reactions");
        el.removeAttribute("aria-description");
      }
    };
  }, [track, rows]);

  return null;
}

function relativeTime(at: number): string {
  const sec = Math.max(0, Math.round((Date.now() - at) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hour = Math.round(min / 60);
  if (hour < 24) return `${hour}h ago`;
  const day = Math.round(hour / 24);
  return `${day}d ago`;
}

export function ReactionPanel(props: {
  bookId: string;
  editionId: string;
  anchorSid: string;
  roomCode?: string | null;
  busy?: boolean;
  onBusy?(busy: boolean): void;
  onBack(): void;
  onNotice?(message: string): void;
  onRoomReaction?(emoji: string, sid: string): void;
}): ReactElement {
  const {
    bookId,
    editionId,
    anchorSid,
    roomCode = null,
    busy: outerBusy = false,
    onBusy,
    onBack,
    onNotice,
    onRoomReaction,
  } = props;
  const blockId = blockIdFrom(anchorSid);
  const [kind, setKind] = useState<ReactionTargetKind>("sentence");
  const [rows, setRows] = useState<ReaderReaction[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const target = useMemo(
    () => ({ kind, id: kind === "sentence" ? anchorSid : (blockId ?? anchorSid) }),
    [kind, anchorSid, blockId],
  );

  const load = useCallback(async () => {
    setError(null);
    try {
      const response = await reactionApi.listTarget(bookId, editionId, target, roomCode);
      setRows(response.reactions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load the reactions.");
    }
  }, [bookId, editionId, target, roomCode]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  const mine = rows?.find((row) => row.mine) ?? null;
  const busy = outerBusy || writing;

  const choose = useCallback(
    async (value: ReactionValue) => {
      setWriting(true);
      onBusy?.(true);
      try {
        const response =
          mine?.reaction === value
            ? await reactionApi.remove(bookId, editionId, target, roomCode)
            : await reactionApi.put(bookId, editionId, target, value, roomCode);
        setRows(response.reactions);
        announceChange(bookId, editionId);
        if (mine?.reaction === value) {
          onNotice?.("Reaction removed.");
        } else {
          const meta = reactionMeta(value);
          onNotice?.(`${meta.emoji} saved — everyone who can read this book can see it.`);
          onRoomReaction?.(meta.emoji, anchorSid);
        }
      } catch (err) {
        onNotice?.(err instanceof ApiError ? err.message : "That reaction did not save.");
      } finally {
        setWriting(false);
        onBusy?.(false);
      }
    },
    [bookId, editionId, target, roomCode, mine?.reaction, anchorSid, onBusy, onNotice, onRoomReaction],
  );

  return (
    <div className="ab-sm-panel">
      <div className="ab-sm-head">
        <span className="ab-sm-title">React to this passage</span>
      </div>
      <p className="ab-sm-note ab-sm-quiet">
        Reactions stay with this edition and are visible to anyone who can read the book. In a reading room, they also pop up right away.
      </p>
      <div className="ab-sm-row" role="group" aria-label="Reaction target" style={{ justifyContent: "flex-start" }}>
        <button type="button" className="ab-sm-cancel" aria-pressed={kind === "sentence"} disabled={busy} onClick={() => setKind("sentence")}>
          Sentence
        </button>
        <button type="button" className="ab-sm-cancel" aria-pressed={kind === "block"} disabled={busy || blockId === null} onClick={() => setKind("block")}>
          Paragraph
        </button>
      </div>
      <div className="ab-sm-row" role="group" aria-label={`React to this ${kind === "sentence" ? "sentence" : "paragraph"}`} style={{ justifyContent: "flex-start" }}>
        {REACTIONS.map((reaction) => (
          <button
            key={reaction.id}
            type="button"
            className="ab-sm-cancel"
            aria-label={mine?.reaction === reaction.id ? `Remove ${reaction.label} reaction` : reaction.label}
            aria-pressed={mine?.reaction === reaction.id}
            title={reaction.label}
            disabled={busy}
            onClick={() => void choose(reaction.id)}
            style={{ minWidth: "2.75rem", fontSize: "1.2rem" }}
          >
            <span aria-hidden="true">{reaction.emoji}</span>
          </button>
        ))}
      </div>

      {rows === null ? <p className="ab-sm-note" role="status">Loading reactions…</p> : null}
      {error ? <p className="ab-sm-note ab-sm-bad" role="alert">{error}</p> : null}
      {rows && rows.length === 0 ? <p className="ab-sm-note">No reactions yet.</p> : null}
      {rows && rows.length > 0 ? (
        <ul className="ab-sm-takes" aria-label="Reader reactions" aria-live="polite">
          {rows.map((row, index) => {
            const meta = reactionMeta(row.reaction);
            return (
              <li key={`${row.targetKind}:${row.targetId}:${index}`} className="ab-sm-take" style={{ gridTemplateColumns: "2.25rem 1fr" }}>
                <span aria-hidden="true" style={{ fontSize: "1.2rem", textAlign: "center" }}>{meta.emoji}</span>
                <span>
                  <span className="ab-sm-take-name">{row.mine ? "You" : row.actor.name}</span>
                  <span className="ab-sm-take-meta" style={{ display: "block" }}>{meta.label} · {relativeTime(row.updatedAt)}</span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="ab-sm-row">
        <button type="button" className="ab-sm-cancel" disabled={busy} onClick={onBack}>Back</button>
      </div>
    </div>
  );
}

export default PersistentReactionMarks;
